<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5">
      <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-layer-group"
        :showAction="true"
        actionLabel="Create Module"
        :modal="'createNew'"
        :disabled="!canCreate('settings')"
      />
      <CommonTable2
        :tableObj="tableConfig"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="settings"
      />
    </div>
  </div>

  <!-- pagination -->
  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>

      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    saveButtonText="Create"
  >
    <template #body>
      <BaseForm
        :fields="moduleFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
      />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
  >
    <template #body>
      <BaseForm
        :fields="moduleFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";
const { canCreate } = usePermissions();

const API_RESOURCE = "/cms-roles-permissions/modules";
const pageTitle = "Permission Module";
const title = "Permission Modules Management";

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// pagination local UI state
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

// visiblePages computed
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Key", field: "key", sortable: true },
    { name: "Created At", field: "created_at", sortable: true },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  showStatusFilter: false,
  table_width: "100%",
});

watch(crud.data, (newVal) => {
  rows.value = newVal || [];
});

watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;
    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
      tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const changeRows = async () => {
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  title: "",
  key: "",
});

const editingItemData = ref<any>({
  _id: "",
  title: "",
  key: "",
});

// --- Form Definitions ---
const moduleFormFields = computed<FormField[]>(() => [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter module title",
    readonly: isViewMode.value,
  },
]);

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this module?")) {
    await crud.deleteItem(id);
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const handleSaveCreate = async () => {
  // Auto-generate key from title + random digits
  const baseKey = newItemData.value.title.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_');
  const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4 digit random
  newItemData.value.key = `${baseKey}_${randomSuffix}`;

  const result = await crud.createItem(newItemData.value);
  if (result) {
    closeCreateModal();
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, created_at, updated_at, ...updatePayload } = editingItemData.value;

  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = {
    title: "",
    key: "",
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
};

// --- lifecycle ---
onMounted(async () => {
  await crud.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});
</script>

<style scoped>
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}
.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }
.page-btn:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active { background: #21c1d6; color: #fff; border: 1px solid #21c1d6; font-weight: 600; }
.prev_btn, .next_btn { color: #21c1d6; }
</style>
